/** The longest edge a photographed slip is kept at. A transfer receipt stays readable well below
 *  this; a phone camera produces four to six times it, for no gain to whoever reads the slip. */
const MAX_EDGE = 2000;

/** WebP at this quality holds a slip's small print and lands around a twentieth of the original. */
const WEBP_QUALITY = 0.82;

/** Anything at or below this uploads in a moment on a slow line and is left exactly as it is. */
const SHRINK_ABOVE_BYTES = 600 * 1024;

/**
 * Get a picked file ready to send.
 *
 * Two things go wrong when a raw phone photo is handed to `fetch`. It streams the file off the
 * disk as the request goes out, so a file the browser cannot finish reading ends the request body
 * in the middle of the multipart form — the API then reports a half-arrived upload, long after
 * the person has finished waiting. And a five-megabyte photo on the school's uplink simply takes
 * longer than the request is allowed to live, which fails the same way for a different reason.
 * The office hit the second: photographed slips failed where a PDF of the same slip went through.
 *
 * So the bytes are read here, and an image that is large enough to be worth it is re-encoded
 * small before anything is sent. A slip does not need to arrive at full camera resolution — it
 * needs to be legible, and legible is a fraction of the size.
 *
 * Whatever cannot be re-encoded is passed through as it stands rather than refused: a PDF has
 * nothing to shrink, and no browser decodes HEIC everywhere, so `sharp` converts those on the
 * API as it always has.
 */
export async function prepareFileForUpload(file: File): Promise<File> {
  const bytes = await readBytes(file);

  if (!file.type.startsWith('image/') || bytes.byteLength <= SHRINK_ABOVE_BYTES) {
    return new File([bytes], file.name, { type: file.type });
  }

  const shrunk = await shrinkImage(new Blob([bytes], { type: file.type }), file.name);
  // Re-encoding can lose: a small PNG screenshot, an image already smaller than the ceiling. The
  // point is a shorter upload, so the bigger of the two is never the right one to send.
  return shrunk && shrunk.size < bytes.byteLength ? shrunk : new File([bytes], file.name, { type: file.type });
}

async function readBytes(file: File): Promise<ArrayBuffer> {
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    throw new Error(`"${file.name}" could not be read. If it is stored in iCloud, open it once and try again.`);
  }
  // A zero-byte read is what an evicted file gives back on some systems rather than an error: the
  // upload "succeeds" and attaches nothing, which is the failure nobody finds out about.
  if (bytes.byteLength === 0) {
    throw new Error(`"${file.name}" is empty. If it is stored in iCloud, open it once and try again.`);
  }
  return bytes;
}

/** Null whenever the browser cannot decode or re-encode this image — HEIC in most of them. */
async function shrinkImage(blob: Blob, name: string): Promise<File | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null;
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const encoded = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(result => resolve(result), 'image/webp', WEBP_QUALITY)
    );
    if (!encoded || encoded.size === 0) return null;
    return new File([encoded], `${stripExtension(name)}.webp`, { type: 'image/webp' });
  } finally {
    bitmap.close();
  }
}

function stripExtension(name: string): string {
  return name.replace(/\.[^./\\]+$/, '') || name;
}
